import moment from 'moment-timezone';
import { allCourtsCheckboxKey } from './courtSchedular';
import { drawEliminationTypes, getPlayersCountListToForwardForPlayoff, feedInBackDrawFormatOptions } from './eventConstants';
import { getFeedOutRoundOptions } from './events';

export const apiDateFormat = 'YYYY-MM-DD';
export const addDivisionSteps = {
    SELECT_DIVISIONS: 'SELECT_DIVISIONS',
    DEFINE_SCORING_FORMATS: 'DEFINE_SCORING_FORMATS',
};
export const divisionScoringFormatKeyMap = {
    roundRobin: drawEliminationTypes.ROUND_ROBIN,
    singleElimination: drawEliminationTypes.SINGLE_ELIMINATION,
    doubleElimination: drawEliminationTypes.DOUBLE_ELIMINATION,
    firstMatchBackdraw: drawEliminationTypes.FIRST_MATCH_BACKDRAW,
    feedInBackdraw: drawEliminationTypes.FEED_IN_BACKDRAW,
};

export const getDefaultFacilitiesSelected = (dayConfig) => {
  const facilities = {};

  dayConfig.facilities.forEach((facility) => {
      const courts = {};
      let isAnyCourtNotSelected = false;
      let isAtLeastOneCourtSelected = false;

      facility.courts.forEach((court) => {
          const isCourtSelected = court.selected;
          courts[court.id] = { selected: isCourtSelected };

          isAnyCourtNotSelected = isAnyCourtNotSelected || !isCourtSelected;
          isAtLeastOneCourtSelected = isAtLeastOneCourtSelected || isCourtSelected;
      });

      if (facility.courts.length > 1) {
          courts[allCourtsCheckboxKey] = { selected: !isAnyCourtNotSelected };
      }

      facilities[facility.id] = {
          selected: isAtLeastOneCourtSelected,
          courts,
      };
  });

  if (dayConfig.facilities.length === 1) {
      const facilityId = dayConfig.facilities[0].id;
      facilities[facilityId].selected = true;
  }

  return facilities;
};

export const combineDivisions = (selectedDivisions, eliminationTypeId) => {
    const newDivId = selectedDivisions.reduce((acc, div, index) => {
        return index === 0 ? `${div.divisionId}` : `${acc}-${div.divisionId}`;
    }, '');

    const newPriority = selectedDivisions.reduce((acc, div) => {
        if (acc) {
            if (div.priority) {
                return div.priority < acc ? div.priority : acc;
            }

            return acc;
        }

        return div.priority;
    }, null);

    const newEntrantsCount = selectedDivisions.reduce((acc, div) => {
        return parseInt(acc) + parseInt(div.entrants);
    }, 0);

    const newDate = selectedDivisions.reduce((acc, div) => {
        return acc || div.date;
    }, null);

    const newDivisionName = selectedDivisions.reduce((acc, div, index) => {
        return index === 0 ? `${div.name}` : `${acc} & ${div.name}`;
    }, '');

    const combinedDivisionIds = selectedDivisions.reduce((acc, division) => {
        return acc.concat(division.divisionIds);
    }, []);

    return {
        selected: true,
        disableDrawLevelChanges: false,
        name: newDivisionName,
        divisionId: newDivId,
        divisionIds: combinedDivisionIds,
        eliminationTypeId,
        priority: newPriority,
        entrants: newEntrantsCount,
        maxCourts: null,
        startTime: null,
        date: newDate,
        drawId: null,
        virtualDrawId: null,
    };
};

export const areScoreFormatPrioritiesValid = (virtualDraws = []) => {
    const selectedDrawsPriorities = virtualDraws
        .filter((vd) => vd.selected)
        .map((vd) => parseInt(vd.priority))
        .filter((priority) => priority > 0)
        .sort((a, b) => a - b);

    if (selectedDrawsPriorities.length > 0) {
        if (selectedDrawsPriorities[0] !== 1) {
            return false;
        }

        const uniquePriorities = selectedDrawsPriorities.reduce((acc, priority) => {
            acc.set(priority, priority);
            return acc;
        }, new Map()).values();
        const uniquePrioritiesList = [
            ...uniquePriorities,
        ];

        return uniquePrioritiesList.length === selectedDrawsPriorities.length;
    }

    return true;
};

export const playOffDrawDetails = [
    {
        id: 'roundRobinPlayOffDraw',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        fieldType: 'checkbox',
        fieldLabel: 'Add playoff draw',
        fieldName: 'addPlayoffDraw',
        needsExtraFieldsToCheck: false,
        extraFields: null,
        fieldsToClear: [
            {
                name: 'playoffDrawAdvanceEntrantsCount',
                type: 'number',
            },
            {
                name: 'addBronzeMedalMatch',
                type: 'boolean',
            },
            {
                name: 'overrideScoringBronze',
                type: 'boolean',
            },
            {
                name: 'bronzeScoringTypeId',
                type: 'number',
            },
            {
                name: 'bronzeAllottedMatchTime',
                type: 'number',
            },
        ],
    },
    {
        id: 'roundRobinPlayoffDrawAdvanceEntrantsCount',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        fieldType: 'select',
        fieldLabel: 'Number of players/teams to advance',
        fieldOptions: getPlayersCountListToForwardForPlayoff(2, 32),
        fieldName: 'playoffDrawAdvanceEntrantsCount',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'roundRobinPlayOffDraw',
            fieldToCheck: 'addPlayoffDraw',
            operation: 'equals',
            value: true,
        }],
        fieldsToClear: [
            {
                name: 'addBronzeMedalMatch',
                type: 'boolean',
            },
            {
                name: 'overrideScoringBronze',
                type: 'boolean',
            },
            {
                name: 'bronzeScoringTypeId',
                type: 'number',
            },
            {
                name: 'bronzeAllottedMatchTime',
                type: 'number',
            },
        ],
    },
    {
        id: 'roundRobinBronzeMedalMatch',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        fieldType: 'checkbox',
        fieldLabel: 'Add bronze medal playoff match',
        fieldName: 'addBronzeMedalMatch',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'roundRobinPlayoffDrawAdvanceEntrantsCount',
            fieldToCheck: 'playoffDrawAdvanceEntrantsCount',
            operation: 'greaterThanEqualTo',
            value: 4,
        }],
        fieldsToClear: [
            {
                name: 'overrideScoringBronze',
                type: 'boolean',
            },
            {
                name: 'bronzeScoringTypeId',
                type: 'number',
            },
            {
                name: 'bronzeAllottedMatchTime',
                type: 'number',
            },
        ],
    },
    {
        id: 'singleEliminationThreeFourPlayoffDraw',
        drawTypeKey: 'singleElimination',
        drawTypeValue: 'single_elimination',
        fieldType: 'checkbox',
        fieldLabel: 'Add 3-4 draw',
        fieldName: 'addThreeFourDraw',
        needsExtraFieldsToCheck: false,
        extraFields: null,
        fieldsToClear: [
            {
                name: 'overrideScoringThreeFour',
                type: 'boolean',
            },
            {
                name: 'threeFourPlayoffScoringTypeId',
                type: 'number',
            },
            {
                name: 'threeFourPlayoffAllottedMatchTime',
                type: 'number',
            },
        ],
    },
    {
        id: 'firstMatchBackdrawIsQualifier',
        drawTypeKey: 'firstMatchBackDraw',
        drawTypeValue: 'first_match_backdraw',
        fieldType: 'checkbox',
        fieldLabel: 'Qualifier draw',
        fieldName: 'isQualifierDraw',
        needsExtraFieldsToCheck: false,
        extraFields: null,
        fieldsToClear: [
            {
                name: 'firstMatchBackDrawFeedOutRound',
                type: 'number',
            },
        ],
    },
    {
        id: 'firstMatchBackDrawFeedOutRound',
        drawTypeKey: 'firstMatchBackDraw',
        drawTypeValue: 'first_match_backdraw',
        fieldType: 'select',
        fieldLabel: 'Feed-In Qualifier Cutoff Round',
        fieldOptions: getFeedOutRoundOptions(128),
        fieldName: 'feedOutRound',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'firstMatchBackdrawIsQualifier',
            fieldToCheck: 'isQualifierDraw',
            operation: 'equals',
            value: true,
        }],
        fieldsToClear: null,
    },
    {
        id: 'feedInBackdrawFeedoutRound',
        drawTypeKey: 'feedInBackdraw',
        drawTypeValue: 'feed_in_backdraw',
        fieldType: 'select',
        fieldLabel: 'Feed-in backdraw format',
        fieldOptions: feedInBackDrawFormatOptions,
        fieldName: 'feedOutRound',
        needsExtraFieldsToCheck: false,
        extraFields: null,
        fieldsToClear: null,
    },
    {
        id: 'feedInBackdrawBronzeMedalMatch',
        drawTypeKey: 'feedInBackdraw',
        drawTypeValue: 'feed_in_backdraw',
        fieldType: 'checkbox',
        fieldLabel: 'Add 3-4 draw',
        fieldName: 'addBronzeMedalMatch',
        needsExtraFieldsToCheck: false,
        extraFields: null,
        fieldsToClear: [
            {
                name: 'overrideScoringBronze',
                type: 'boolean',
            },
            {
                name: 'bronzeScoringTypeId',
                type: 'number',
            },
            {
                name: 'bronzeAllottedMatchTime',
                type: 'number',
            },
        ],
    },
];

export const primaryViews = {
    CapacitySimulator: 'CapacitySimulator',
    DraftDraws: 'DraftDraws',
};

export const scoringTypeOverrides = [
    {
        id: 'roundRobinPlayoffDrawOverride',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        field: 'overrideScoringPlayoff',
        label: 'Override playoff scoring',
        scoringFormatField: 'playoffScoringTypeId',
        allottedMatchTimeField: 'playoffAllottedMatchTime',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'roundRobinPlayOffDraw',
            fieldToCheck: 'addPlayoffDraw',
            operation: 'equals',
            value: true,
        }],
    },
    {
        id: 'roundRobinGoldMatchOverride',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        field: 'overrideScoringGold',
        label: 'Override gold medal scoring',
        scoringFormatField: 'goldScoringTypeId',
        allottedMatchTimeField: 'goldAllottedMatchTime',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'roundRobinPlayOffDraw',
            fieldToCheck: 'addPlayoffDraw',
            operation: 'equals',
            value: true,
        }],
    },
    {
        id: 'roundRobinBronzeMatchOverride',
        drawTypeKey: 'roundRobin',
        drawTypeValue: 'round_robin',
        field: 'overrideScoringBronze',
        label: 'Override bronze medal scoring',
        scoringFormatField: 'bronzeScoringTypeId',
        allottedMatchTimeField: 'bronzeAllottedMatchTime',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'roundRobinPlayOffDraw',
            fieldToCheck: 'addBronzeMedalMatch',
            operation: 'equals',
            value: true,
        }],
    },
    {
        id: 'doubleEliminationBackDraw',
        drawTypeKey: 'doubleElimination',
        drawTypeValue: 'double_elimination',
        field: 'overrideScoringBackdraw',
        label: 'Override back draw(s) scoring',
        scoringFormatField: 'backdrawScoringTypeId',
        allottedMatchTimeField: 'backdrawAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'doubleEliminationGoldMatch',
        drawTypeKey: 'doubleElimination',
        drawTypeValue: 'double_elimination',
        field: 'overrideScoringGoldSilver',
        label: 'Override gold/silver medal round scoring',
        scoringFormatField: 'goldSilverScoringTypeId',
        allottedMatchTimeField: 'goldSilverAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'doubleEliminationBronzeMatch',
        drawTypeKey: 'doubleElimination',
        drawTypeValue: 'double_elimination',
        field: 'overrideScoringBronze',
        label: 'Override bronze medal round scoring',
        scoringFormatField: 'bronzeScoringTypeId',
        allottedMatchTimeField: 'bronzeAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'singleEliminationFinals',
        drawTypeKey: 'singleElimination',
        drawTypeValue: 'single_elimination',
        field: 'overrideScoringFinals',
        label: 'Override finals scoring',
        scoringFormatField: 'threeFourFinalsScoringTypeId',
        allottedMatchTimeField: 'threeFourFinalsAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'singleEliminationThreeFourOverride',
        drawTypeKey: 'singleElimination',
        drawTypeValue: 'single_elimination',
        field: 'overrideScoringThreeFour',
        label: 'Override 3-4 playoff scoring',
        scoringFormatField: 'threeFourPlayoffScoringTypeId',
        allottedMatchTimeField: 'threeFourPlayoffAllottedMatchTime',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'singleEliminationThreeFourPlayoffDraw',
            fieldToCheck: 'addThreeFourDraw',
            operation: 'equals',
            value: true,
        }],
    },
    // first match back draw
    {
        id: 'firstMatchBackDrawBackDraw',
        drawTypeKey: 'firstMatchBackDraw',
        drawTypeValue: 'first_match_backdraw',
        field: 'overrideScoringBackdraw',
        label: 'Override back draw(s) scoring',
        scoringFormatField: 'backdrawScoringTypeId',
        allottedMatchTimeField: 'backdrawAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'firstMatchBackDrawFinals',
        drawTypeKey: 'firstMatchBackDraw',
        drawTypeValue: 'first_match_backdraw',
        field: 'overrideScoringGoldSilver',
        label: 'Override gold medal scoring',
        scoringFormatField: 'goldSilverScoringTypeId',
        allottedMatchTimeField: 'goldSilverAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    // feed in back draw
    {
        id: 'feedInBackdrawBackdraw',
        drawTypeKey: 'feedInBackdraw',
        drawTypeValue: 'feed_in_backdraw',
        field: 'overrideScoringBackdraw',
        label: 'Override back draw(s) scoring',
        scoringFormatField: 'backdrawScoringTypeId',
        allottedMatchTimeField: 'backdrawAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'feedInBackdrawGoldMatch',
        drawTypeKey: 'feedInBackdraw',
        drawTypeValue: 'feed_in_backdraw',
        field: 'overrideScoringGoldSilver',
        label: 'Override gold medal scoring',
        scoringFormatField: 'goldSilverScoringTypeId',
        allottedMatchTimeField: 'goldSilverAllottedMatchTime',
        needsExtraFieldsToCheck: false,
        extraFields: null,
    },
    {
        id: 'feedInBackdrawBronzeMatchOverride',
        drawTypeKey: 'feedInBackdraw',
        drawTypeValue: 'feed_in_backdraw',
        field: 'overrideScoringBronze',
        label: 'Override 3-4 playoff scoring',
        scoringFormatField: 'bronzeScoringTypeId',
        allottedMatchTimeField: 'bronzeAllottedMatchTime',
        needsExtraFieldsToCheck: true,
        extraFields: [{
            id: 'feedInBackdrawBronzeMedalMatch',
            fieldToCheck: 'addBronzeMedalMatch',
            operation: 'equals',
            value: true,
        }],
    },
];

export const getMatchCountInRound = (roundNumber, matches = []) => {
    const roundMatches = matches.filter((match) => match.roundNumber === roundNumber);
    return roundMatches.length;
};

export const isMatchScheduled = (match) => {
    return !!match.courtId;
};

export const getScheduledMatchesFromDayPlan = (dayPlan) => {
    const { virtualDraws = [] } = dayPlan || {};
    return virtualDraws.reduce((matches, vd) => {
        if (vd.groups.length > 0) {
            let newMatches = [
                ...matches,
            ];
            vd.groups.forEach((vdg) => {
                const scheduledMatchesInGroup = vdg.matches.filter(isMatchScheduled);
                newMatches = newMatches.concat(scheduledMatchesInGroup);
            });

            return newMatches;
        }

        return matches;
    }, []);
};

export const getDateTimeObjectFromCurrentDateAndTime = (date, time, eventSchedule) => {
    const currentDateObj = moment(date).tz(eventSchedule?.timeZone?.value).startOf('day');
    const timeValue = time.split(':');
    currentDateObj.add(timeValue[0], 'hours');
    currentDateObj.add(timeValue[1], 'minutes');

    return currentDateObj.toISOString();
};
